/**
 * Dietician Confirmation Component (Step 4)
 *
 * Displays registration completion confirmation with summary information,
 * next steps, and download functionality. Provides clear feedback about
 * the approval process and what happens next.
 *
 * @module components/dietician-registration/DieticianConfirmation
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { useTheme } from '@/hooks/useTheme';
import type { HealthCondition } from '@/types/referral';
import { cn } from '@/utils/cn';

export interface ConfirmationData {
  registrationId: string;
  dieticianName: string;
  email: string;
  apdNumber: string;
  areasOfPractice: HealthCondition[];
  submissionDate: string;
  approvalTimeline: string;
  nextSteps: string[];
}

export interface DieticianConfirmationProps {
  registrationData: ConfirmationData;
}

export function DieticianConfirmation(props: DieticianConfirmationProps) {
  // Hooks
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  return (
    <div className="space-y-6">
      {/* Success message */}
      <div className="text-center">
        <h2 className={cn(
          'text-2xl font-bold mb-2',
          isDark ? 'text-white' : 'text-secondary-900'
        )}>
          Registration Complete!
        </h2>

        <p className={cn(
          'text-lg mb-6',
          isDark ? 'text-secondary-300' : 'text-secondary-600'
        )}>
          Thank you for registering as an Accredited Practicing Dietitian with BiteWise.
        </p>
      </div>

      {/* Registration summary */}
      <Card className={cn(
        'border-2',
        isDark ? 'bg-secondary-800 border-gray-600' : 'bg-gray-50 border-gray-200'
      )}>
        <CardBody className="p-6">
          <h3 className={cn(
            'text-lg font-semibold mb-4',
            isDark ? 'text-white' : 'text-secondary-900'
          )}>
            Registration Summary
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 break-words">
            <div className="min-w-0">
              <label className={cn(
                'block text-sm font-medium mb-1',
                isDark ? 'text-secondary-300' : 'text-secondary-700'
              )}>
                Dietitian Name
              </label>
              <p className={cn(
                'text-sm break-words',
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              )}>
                {props.registrationData.dieticianName}
              </p>
            </div>

            <div className="min-w-0">
              <label className={cn(
                'block text-sm font-medium mb-1',
                isDark ? 'text-secondary-300' : 'text-secondary-700'
              )}>
                APD Number
              </label>
              <p className={cn(
                'text-sm break-words',
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              )}>
                {props.registrationData.apdNumber}
              </p>
            </div>

            <div className="min-w-0">
              <label className={cn(
                'block text-sm font-medium mb-1',
                isDark ? 'text-secondary-300' : 'text-secondary-700'
              )}>
                Submission Date
              </label>
              <p className={cn(
                'text-sm break-words',
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              )}>
                {formatDate(props.registrationData.submissionDate)}
              </p>
            </div>

            <div className="min-w-0 sm:col-span-2">
              <label className={cn(
                'block text-sm font-medium mb-2',
                isDark ? 'text-secondary-300' : 'text-secondary-700'
              )}>
                Areas of Practice
              </label>
              <div className="flex flex-wrap gap-2">
                {props.registrationData.areasOfPractice.map((area, index) => (
                  <span
                    key={index}
                    className={cn(
                      'px-3 py-1 rounded-full text-xs font-medium',
                      isDark
                        ? 'bg-blue-900 text-blue-200'
                        : 'bg-blue-100 text-blue-800'
                    )}
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Next steps */}
      <Card>
        <CardBody className="p-6">
          <h3 className={cn(
            'text-lg font-semibold mb-4',
            isDark ? 'text-white' : 'text-secondary-900'
          )}>
            What Happens Next?
          </h3>

          <div className={cn(
            'p-4 rounded-lg mb-4',
            isDark ? 'bg-blue-900 bg-opacity-50' : 'bg-blue-50'
          )}>
            <p className={cn(
              'text-sm font-medium',
              isDark ? 'text-blue-200' : 'text-blue-800'
            )}>
              Expected approval timeline: {props.registrationData.approvalTimeline}
            </p>
          </div>

          <ol className="space-y-3">
            {props.registrationData.nextSteps.map((step, index) => (
              <li key={index} className="flex items-start">
                <span className={cn(
                  'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium mr-3',
                  isDark
                    ? 'bg-secondary-700 text-secondary-300'
                    : 'bg-secondary-50 text-secondary-700'
                )}>
                  {index + 1}
                </span>
                <span className={cn(
                  'text-sm',
                  isDark ? 'text-secondary-300' : 'text-secondary-700'
                )}>
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </CardBody>
      </Card>

      {/* Action buttons */}
      <div className="flex justify-center">
        <Button
          onClick={() => {
            // Reset navigation stack to home, then navigate to dashboard
            navigate('/', { replace: true });
            navigate('/dashboard');
          }}
        >
          Go to Dashboard
        </Button>
      </div>

      {/* Support information */}
      <div className="text-center">
        <p className={cn(
          'text-sm',
          isDark ? 'text-secondary-400' : 'text-secondary-600'
        )}>
          Need help? Contact our support team at{' '}
          <a
            href="mailto:admin@bitewise.com.au"
            className="text-blue-600 hover:underline"
          >
            admin@bitewise.com.au
          </a>
        </p>
      </div>
    </div>
  );
}